(() => {
  const OPEN_TRIGGER_SELECTOR = '[data-filter-drawer-open]';

  const initSortDropdown = () => {
    const dropdown = document.querySelector('[data-sort-dropdown]');
    if (!dropdown || dropdown.dataset.bound === 'true') return;

    const trigger = dropdown.querySelector('[data-sort-trigger]');
    const list = dropdown.querySelector('[data-sort-list]');
    const label = dropdown.querySelector('[data-sort-label]');
    const input = dropdown.querySelector('[data-sort-input]');
    const options = Array.from(dropdown.querySelectorAll('[role="option"]'));
    if (!trigger || !list || !input || !options.length) return;

    let activeIndex = Math.max(0, options.findIndex((option) => option.getAttribute('aria-selected') === 'true'));

    const setActive = (index) => {
      options[activeIndex]?.removeAttribute('data-active');
      activeIndex = (index + options.length) % options.length;
      options[activeIndex].setAttribute('data-active', '');
      options[activeIndex].scrollIntoView({ block: 'nearest' });
    };

    const isOpen = () => dropdown.hasAttribute('data-open');

    const open = () => {
      dropdown.setAttribute('data-open', '');
      trigger.setAttribute('aria-expanded', 'true');
      setActive(activeIndex);
      list.focus({ preventScroll: true });
    };

    const close = (returnFocus = true) => {
      if (!isOpen()) return;
      dropdown.removeAttribute('data-open');
      trigger.setAttribute('aria-expanded', 'false');
      options[activeIndex]?.removeAttribute('data-active');
      if (returnFocus) trigger.focus({ preventScroll: true });
    };

    const select = (option) => {
      const { value } = option.dataset;
      close();
      if (value === input.value) return;

      options.forEach((item) => item.setAttribute('aria-selected', String(item === option)));
      input.value = value;
      if (label) label.textContent = option.textContent.trim();
      trigger.setAttribute('aria-label', `Ordenar por: ${option.textContent.trim()}`);

      const form = input.form || document.getElementById(input.getAttribute('form'));
      if (form) form.requestSubmit();
    };

    trigger.addEventListener('click', () => (isOpen() ? close() : open()));

    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        open();
      }
    });

    options.forEach((option, index) => {
      option.addEventListener('click', () => select(option));
      option.addEventListener('mousemove', () => {
        if (index !== activeIndex) setActive(index);
      });
    });

    list.addEventListener('keydown', (event) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setActive(activeIndex + 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setActive(activeIndex - 1);
          break;
        case 'Home':
          event.preventDefault();
          setActive(0);
          break;
        case 'End':
          event.preventDefault();
          setActive(options.length - 1);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          select(options[activeIndex]);
          break;
        case 'Escape':
          event.preventDefault();
          close();
          break;
        case 'Tab':
          close(false);
          break;
      }
    });

    document.addEventListener('click', (event) => {
      if (!dropdown.contains(event.target)) close(false);
    });

    dropdown.dataset.bound = 'true';
  };

  const formatMoney = (cents, format) => {
    const value = (cents / 100).toFixed(2);
    const [dollars, decimals] = value.split('.');
    const withThousands = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return format
      .replace(/\{\{\s*amount\s*\}\}/, `${withThousands}.${decimals}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withThousands)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${withThousands.replace(/,/g, '.')},${decimals}`)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, withThousands.replace(/,/g, '.'));
  };

  const initPriceRange = (wrapper) => {
    if (!wrapper || wrapper.dataset.bound === 'true') return;

    const fill = wrapper.querySelector('[data-price-fill]');
    const minSlider = wrapper.querySelector('[data-price-slider="min"]');
    const maxSlider = wrapper.querySelector('[data-price-slider="max"]');
    const minLabel = wrapper.querySelector('[data-price-label="min"]');
    const maxLabel = wrapper.querySelector('[data-price-label="max"]');
    const minInput = wrapper.querySelector('[data-price-input="min"]');
    const maxInput = wrapper.querySelector('[data-price-input="max"]');

    if (!fill || !minSlider || !maxSlider) return;

    const rangeMax = Number(wrapper.dataset.rangeMax) || Number(minSlider.max) || 0;
    const moneyFormat = wrapper.dataset.moneyFormat || 'R$ {{amount}}';

    const update = () => {
      let min = Number(minSlider.value);
      let max = Number(maxSlider.value);

      if (min > max) {
        [min, max] = [max, min];
        minSlider.value = String(min);
        maxSlider.value = String(max);
      }

      const minPercent = rangeMax > 0 ? (min / rangeMax) * 100 : 0;
      const maxPercent = rangeMax > 0 ? (max / rangeMax) * 100 : 100;

      fill.style.left = `${minPercent}%`;
      fill.style.right = `${100 - maxPercent}%`;

      if (minLabel) minLabel.textContent = formatMoney(min * 100, moneyFormat);
      if (maxLabel) maxLabel.textContent = formatMoney(max * 100, moneyFormat);
      if (minInput) minInput.value = String(min);
      if (maxInput) maxInput.value = String(max);
    };

    // Price inputs start disabled (not submitted) when no price filter is active;
    // enable them only once the shopper actually moves a slider.
    const handleSliderInput = () => {
      if (minInput) minInput.disabled = false;
      if (maxInput) maxInput.disabled = false;
      update();
    };

    minSlider.addEventListener('input', handleSliderInput);
    maxSlider.addEventListener('input', handleSliderInput);

    update();
    wrapper.dataset.bound = 'true';
  };

  const initAllPriceRanges = () => {
    document.querySelectorAll('[data-price-range]').forEach(initPriceRange);
  };

  const initDrawer = () => {
    initSortDropdown();
    initAllPriceRanges();

    const drawer = document.querySelector('[data-filter-drawer]');
    const overlay = document.querySelector('[data-filter-drawer-overlay]');
    const closeButtons = document.querySelectorAll('[data-filter-drawer-close]');
    const openTriggers = document.querySelectorAll(OPEN_TRIGGER_SELECTOR);

    if (!drawer || !overlay || drawer.dataset.bound === 'true') return;

    const open = () => {
      drawer.hidden = false;
      overlay.hidden = false;
      // Force layout so the transition runs instead of jump-cutting to the open state.
      drawer.getBoundingClientRect();
      drawer.setAttribute('data-open', '');
      overlay.setAttribute('data-open', '');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };

    const close = () => {
      drawer.removeAttribute('data-open');
      overlay.removeAttribute('data-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    const handleTransitionEnd = (event) => {
      if (event.propertyName !== 'transform') return;
      if (!drawer.hasAttribute('data-open')) {
        drawer.hidden = true;
        overlay.hidden = true;
      }
    };

    openTriggers.forEach((trigger) => trigger.addEventListener('click', open));
    closeButtons.forEach((button) => button.addEventListener('click', close));
    overlay.addEventListener('click', close);
    drawer.addEventListener('transitionend', handleTransitionEnd);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.hasAttribute('data-open')) close();
    });

    drawer.dataset.bound = 'true';
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDrawer);
  } else {
    initDrawer();
  }

  document.addEventListener('shopify:section:load', initDrawer);
})();
